// Timeline eventi del ticket
import { useQuery } from '@tanstack/react-query'
import { User, Clock, ArrowRight, MessageSquare, Paperclip, CheckCircle, AlertTriangle } from 'lucide-react'
import { Skeleton } from '../../../components/ui/Skeleton'
import { formatDate } from '../../../lib/utils'
import api from '../../../lib/api'

interface TimelineEvent {
  id: string
  type: 'created' | 'assigned' | 'status_changed' | 'comment' | 'attachment' | 'escalated' | 'completed'
  message: string
  userId: string
  userName: string
  createdAt: string
  metadata?: Record<string, string>
}

const EVENT_ICONS: Record<TimelineEvent['type'], React.ReactNode> = {
  created: <Clock className="h-4 w-4" />,
  assigned: <User className="h-4 w-4" />,
  status_changed: <ArrowRight className="h-4 w-4" />,
  comment: <MessageSquare className="h-4 w-4" />,
  attachment: <Paperclip className="h-4 w-4" />,
  escalated: <AlertTriangle className="h-4 w-4 text-[var(--fc-danger)]" />,
  completed: <CheckCircle className="h-4 w-4 text-[var(--fc-success)]" />,
}

interface TicketTimelineProps {
  ticketId: string
}

export function TicketTimeline({ ticketId }: TicketTimelineProps) {
  const { data: events, isLoading } = useQuery({
    queryKey: ['ticket-timeline', ticketId],
    queryFn: async () => {
      try {
        const res = await api.get<TimelineEvent[]>(`/tickets/${ticketId}/timeline`)
        return res.data ?? []
      } catch {
        return []
      }
    },
    enabled: Boolean(ticketId),
  })

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex gap-3">
            <Skeleton width={32} height={32} rounded />
            <div className="flex-1 space-y-1">
              <Skeleton height={14} className="w-48" />
              <Skeleton height={12} className="w-24" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (!events || events.length === 0) {
    return (
      <p className="py-4 text-center text-sm text-[var(--fc-foreground-muted)]">
        Nessun evento registrato
      </p>
    )
  }

  return (
    <ol className="relative" aria-label="Timeline ticket">
      {events.map((event, index) => (
        <li key={event.id} className="flex gap-3 pb-6 last:pb-0">
          {/* Linea verticale */}
          <div className="flex flex-col items-center">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 border-[var(--fc-border)] bg-[var(--fc-surface)] text-[var(--fc-foreground-muted)]">
              {EVENT_ICONS[event.type]}
            </div>
            {index < events.length - 1 && (
              <div className="w-0.5 flex-1 bg-[var(--fc-border)] mt-1" aria-hidden="true" />
            )}
          </div>

          {/* Contenuto */}
          <div className="flex-1 pb-1 pt-1">
            <p className="text-sm text-[var(--fc-foreground)]">{event.message}</p>
            <div className="mt-1 flex items-center gap-2 text-xs text-[var(--fc-foreground-muted)]">
              <span>{event.userName}</span>
              <span aria-hidden="true">·</span>
              <time dateTime={event.createdAt}>{formatDate(event.createdAt)}</time>
            </div>
          </div>
        </li>
      ))}
    </ol>
  )
}
